// @flow
import { isAfter, endOfQuarter } from 'date-fns';
import BaseModel from 'Stores/BaseModel';
import ListModel from 'Stores/ListModel';
import AssetModel from 'Stores/AssetModel';
import TeaserAreaModel from 'Stores/TeaserAreaModel';
import { formatDate, fromApiDate, stripTags, priceMap, formatDateToISO } from 'Utils';
import {
  validateLength,
  validateRTELength,
  validateUrl,
  validateImageSelect,
  isInvalid,
  injectUrlVars,
  validateDaysBetween,
} from 'Utils/Validators';
import axios from 'axios';
import { withoutKeys } from 'Utils/mutateObject';

declare var API_BASE_URL: string;

export default class TeaserModel extends BaseModel {
  id: number = 0;
  custom: boolean = true;
  headline: {
    forceUppercase: boolean,
    level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
    levelVisually?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
    value: string,
  };
  footnote: string = '';
  topic: string = '';
  text: string = '';
  link: Object = {};
  linkUrl: string = '';
  linkTitle: string = '';
  linkTarget: string = '';
  price: number = 0;
  priceText: string = '';
  priceType: string = 'FIXED';
  image: ?AssetModel = null;
  starttime: ?Date = new Date();
  endtime: ?Date = new Date();
  areas: ?ListModel = new ListModel();
  sortIndex: number = 0;
  publishingDateActivated: boolean = false;
  publishingDateStart: ?string = new Date();
  publishingDateEnd: ?string = null;
  managePublishing: string = 'online';
  consumptionText: ?string = null;
  consumptionInfo: ?string = null;
  consumptionInfoCustomDescriptionEnabled: ?boolean = false;
  consumptionInfoCustomDescription: ?string = null;
  topTeaser: boolean = false;

  constructor(data: Object = {}) {
    super();
    this.hydrate(Object.assign({}, data));

    const link = data.link || {};

    this.raw = data;
    this.link = link;
    this.headline = data.headline || {
      forceUppercase: false,
      level: 'h3',
      value: '',
      levelVisually: 'h3',
    };
    this.linkUrl = injectUrlVars(link.href || '');
    this.linkTitle = link.label || '';
    this.linkTarget = link.target || '';
    this.image = data.image ? new AssetModel(data.image) : null;
    this.imageMobile = data.imageMobile ? new AssetModel(data.imageMobile) : null;
    this.starttime = fromApiDate(data.starttime);
    this.endtime = fromApiDate(data.endtime);
    this.areas = data.areas ? new ListModel(data.areas, TeaserAreaModel) : new ListModel();
    this.publishingDateStart = fromApiDate(data.publishingDateStart);
    this.publishingDateEnd = data.publishingDateEnd
      ? fromApiDate(data.publishingDateEnd)
      : endOfQuarter(this.publishingDateStart);

    this.managePublishing =
      this.hidden && !this.isNew()
        ? 'offline'
        : this.publishingDateActivated
          ? 'timeset'
          : 'online';

    this.consumptionText = data.consumptionText;
    this.consumptionInfo = data.consumptionInfo;
    this.consumptionInfoCustomDescriptionEnabled = !!data.consumptionInfoCustomDescriptionEnabled;
    this.consumptionInfoCustomDescription = data.consumptionInfoCustomDescription;
  }

  valid = () => {
    this.errors = {};
    if (this.custom) {
      validateImageSelect(this.errors, 'image', this.image);
      validateLength(this.errors, 'title', stripTags(this.headline?.value), 1, 65);
      validateLength(this.errors, 'topic', this.topic, 1, 25);
      validateRTELength(this.errors, 'text', stripTags(this.text), 1, 330);
      validateLength(this.errors, 'linkTitle', this.linkTitle, 0, 25);

      if (this.linkUrl.length > 0) {
        validateUrl(this.errors, 'linkUrl', this.linkUrl);
      }

      if (this.publishingDateActivated) {
        validateDaysBetween(
          this.errors,
          'publishingDateEnd',
          this.publishingDateStart,
          this.publishingDateEnd
        );
      }
    }

    // wenn eine Fussnote in der Oberfläche eingetragen wurde muss auch der Text eingetragen werden
    // zu dem diese Fussnote gehört, da sonst die Fussnote für sich allein stehen würde
    if (this.footnote.length > 0) {
      validateLength(this.errors, 'priceText', this.priceText, 1, 5000);
    }

    return !isInvalid(this.errors);
  };

  toListItem() {
    const subline = [];

    if (this.publishingDateActivated) {
      subline.push(
        `Sichtbarkeit: ${formatDate(this.publishingDateStart, 'dd.MM.yyyy')} - ${
          this.publishingDateEnd ? formatDate(this.publishingDateEnd, 'dd.MM.yyyy') : 'offen'
        }`
      );
    }

    if (this.isScheduled()) {
      subline.push(`Gültig ab: ${formatDate(this.starttime)}`);
    }

    subline.push(
      this.areas.items.length ? this.areas.map((area) => area.placement) : 'Keine Fläche definiert'
    );

    return {
      id: this.id,
      index: this.sortIndex,
      headline: this.headline?.value,
      subline: subline.join(' | '),
      hidden: this.hidden,
      image: this.image,
      imageMobile: this.imageMobile,
      badge: !this.custom ? 'MMD' : null,
      topTeaser: this.topTeaser,
      model: this,
    };
  }

  save = () => {
    const json = withoutKeys(this.toRequestJSON(), ['custom']);

    if (this.id > 0) {
      return axios
        .patch(`/api/v1/teasers/${this.id}/`, json)
        .then((res) => new TeaserModel({ ...res.data, sortIndex: this.sortIndex }));
    }

    return axios.post('/api/v1/teasers/', json).then((res) => new TeaserModel(res.data));
  };

  static saveCurrentOrder = (items) => {
    axios.patch(`${API_BASE_URL}/teaser-list/`, {
      //Transform request payload to fit api requirements
      teaserSorting: items.map((item, i) => ({
        id: item.id,
        sortIndex: ++i,
      })),
    });

    return items.map((item, i) => {
      item.model.sortIndex = ++i;
      return item.model;
    });
  };

  delete = () => axios.delete(`/api/v1/teasers/${this.id}/`);

  isScheduled = () => !this.custom && isAfter(this.starttime, new Date());

  hasArea = (areaId: number) => this.areas.getItemById(areaId) !== undefined;

  toJSON = () => {
    const json: Object = {
      topic: this.topic,
      text: this.text,
      title: this.headline?.value,
      footnote: this.footnote,
      price: this.hasPrice() ? this.price : 0,
      priceText: this.priceText,
      priceType: this.priceType,
      image: this.image,
      hidden: this.hidden,
      areas: this.areas.map((area) => area.toJSON()),
      link: {
        url: this.linkUrl,
        title: this.linkTitle,
        href: this.linkUrl,
        label: this.linkTitle,
      },
      sortIndex: this.sortIndex,
      publishingDateActivated: this.publishingDateActivated,
      publishingDateStart: this.publishingDateStart,
      publishingDateEnd: this.publishingDateEnd,
      consumptionText: this.consumptionText,
      consumptionInfo: this.consumptionInfo,
      consumptionInfoCustomDescriptionEnabled: !!this.consumptionInfoCustomDescriptionEnabled,
      consumptionInfoCustomDescription: this.consumptionInfoCustomDescription,
    };

    if (this.id > 0) {
      json.id = this.id;
    }

    return json;
  };

  toRequestJSON: Object = () => {
    const res: Object = { ...this.toJSON() };
    // cast date field to ISO 8601 string securely due to formatting errors with
    // timezone when axios uses JSON.stringify() to cast date objects to string
    const dateFields: Array = ['publishingDateStart', 'publishingDateEnd'];
    dateFields.forEach((field) => {
      if (res.hasOwnProperty(field)) {
        res[field] = !!res[field] ? formatDateToISO(res[field]) : null;
      }
    });

    return res;
  };

  static getAll = (params: Object = {}) =>
    axios({
      method: 'get',
      url: '/api/v1/teasers/',
      params,
    }).then((res: { data: Array<Object> }) => new ListModel(res.data, TeaserModel));

  getPriceType = () => {
    if (this.priceType !== 'NONE') {
      return { value: this.priceType, label: priceMap.get(this.priceType) };
    }

    return '';
  };

  hasPrice = () =>
    this.priceType !== 'NONE' && this.priceType !== 'FREE' && this.priceType !== 'INCLUDED';
}
